import React from 'react';
import { type ModalProps } from '@/stores/use-modal-store.ts';
import { useRevalidator } from 'react-router-dom';
import type { Server } from '@/models';
import { API_BASE } from '@/config';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Modal, ModalBody, ModalContent, ModalHeader } from '@heroui/react';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useSocket } from '@/context/socket-context/use-external-socket';
import { localInputToUTC, toLocalDateTimeInput } from '@/utils/date-formatter.ts';
import { useTranslation } from 'react-i18next';

const formSchema = z.object({
  name: z
    .string()
    .min(1, {
      message: 'Event name is required.',
    })
    .max(50, {
      message: 'Event name must be less than 50 characters.',
    }),
  description: z
    .string()
    .min(1, {
      message: 'Description is required.',
    })
    .max(200, {
      message: 'Description must be less than 200 characters.',
    }),
  channelId: z.string().min(1, {
    message: 'Channel is required.',
  }),
  serverId: z.string(),
  startDate: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid start date.',
  }),
  endDate: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid end date.',
  }),
  recurrenceRule: z.enum(['none', 'daily', 'weekly', 'monthly']).optional(),
  recurrenceEndDate: z
    .string()
    .optional()
    .refine((val) => !val || !isNaN(Date.parse(val)), {
      message: 'Invalid recurrence end date.',
    }),
});

/**
 * @experimental
 * @param param0
 * @param param0.isOpen
 * @param param0.onClose
 * @param param0.data
 * @constructor
 *
 * @example
 * openModal('editServerEvent', {
 *   server,
 *   event,
 * });
 */
export const EditServerEventModal: React.FC<ModalProps> = ({ isOpen, onClose, data }) => {
  const { event, server } = data;

  const socket = useSocket();
  const { revalidate } = useRevalidator();
  const { t } = useTranslation();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: event?.name,
      description: event?.description,
      channelId: event?.channelId,
      serverId: server?.id,
      startDate: toLocalDateTimeInput(event.startDate),
      endDate: toLocalDateTimeInput(event.endDate),
      recurrenceRule: event?.recurrenceRule ?? 'none',
      recurrenceEndDate: event?.recurrenceEndDate
        ? toLocalDateTimeInput(event.recurrenceEndDate)
        : '',
    },
  });

  const onSubmit = async (values) => {
    const payload = {
      ...values,
      startDate: localInputToUTC(values.startDate),
      endDate: localInputToUTC(values.endDate),
      recurrenceRule: values.recurrenceRule === 'none' ? null : values.recurrenceRule,
      recurrenceEndDate: values.recurrenceEndDate
        ? localInputToUTC(values.recurrenceEndDate)
        : null,
    };

    const res = await fetch(`${API_BASE}/api/servers/${server?.id}/events/${event?.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const updated = await res.json();

    socket?.send(
      JSON.stringify({
        type: 'event-edit',
        payload: updated,
      })
    );

    toast.success('Updated');
    revalidate();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onClose}>
      <ModalContent>
        <ModalHeader>Edit event</ModalHeader>

        <ModalBody>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-bold uppercase">
                      {t('ServerEventsPage.eventName')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('ServerEventsPage.eventNamePlaceholder')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-bold uppercase">
                      {t('ServerEventsPage.description')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('ServerEventsPage.descriptionPlaceholder')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="channelId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-bold uppercase">
                      {t('ServerEventsPage.channel')}
                    </FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        className="w-full rounded border bg-background p-2"
                      >
                        <option value="">{t('ServerEventsPage.selectChannelPlaceholder')}</option>
                        {audioChannels?.map((channel) => (
                          <option key={channel.id} value={channel.id}>
                            {channel.name} (Audio)
                          </option>
                        ))}
                        {videoChannels?.map((channel) => (
                          <option key={channel.id} value={channel.id}>
                            {channel.name} (Video)
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-bold uppercase">
                      {t('ServerEventsPage.startDate')}
                    </FormLabel>
                    <FormControl>
                      <input
                        {...field}
                        type="datetime-local"
                        disabled={isLoading}
                        className="w-full rounded border p-2"
                      />
                    </FormControl>
                    <FormDescription>Local time ({timezone})</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-bold uppercase">
                      {t('ServerEventsPage.endDate')}
                    </FormLabel>
                    <FormControl>
                      <input
                        {...field}
                        type="datetime-local"
                        className="w-full rounded border p-2"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="recurrenceRule"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-bold uppercase">
                      {t('ServerEventsPage.recurrence.title')}
                    </FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        className="w-full rounded border bg-background p-2"
                      >
                        <option value="none">{t('ServerEventsPage.recurrence.no_repeat')}</option>
                        <option value="daily">{t('ServerEventsPage.recurrence.daily')}</option>
                        <option value="weekly">{t('ServerEventsPage.recurrence.weekly')}</option>
                        <option value="monthly">{t('ServerEventsPage.recurrence.monthly')}</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="recurrenceEndDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-bold uppercase">
                      {t('ServerEventsPage.repeat_until')}
                    </FormLabel>
                    <FormControl>
                      <input
                        {...field}
                        type="datetime-local"
                        className="w-full rounded border p-2"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit">Save</Button>
            </form>
          </Form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};